/**
 * Expense Edit Modal (T167)
 * Edit expense with audit trail (30-day edit window enforced by backend)
 */

import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useExpense, useUpdateExpense } from '../../hooks/useExpenses';

interface Props {
  expenseId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = [
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'SOFTWARE_SUBSCRIPTIONS', label: 'Software Subscriptions' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services' },
  { value: 'PROFESSIONAL_DEVELOPMENT', label: 'Professional Development' },
  { value: 'RENT', label: 'Rent' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'MEALS', label: 'Meals' },
  { value: 'OTHER', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: '', label: 'Select method (optional)' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Check' },
  { value: 'APPLE_PAY', label: 'Apple Pay' },
  { value: 'GOOGLE_PAY', label: 'Google Pay' },
  { value: 'OTHER', label: 'Other' },
];

export default function ExpenseEditModal({ expenseId, onClose, onSuccess }: Props) {
  const { data: expense, isLoading: loadingExpense } = useExpense(expenseId);
  const updateExpense = useUpdateExpense();

  const [form, setForm] = useState({
    amount: '',
    date: '',
    vendor: '',
    category: '',
    paymentMethod: '',
    description: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (expense) {
      setForm({
        amount: expense.amount.toString(),
        date: new Date(expense.date).toISOString().split('T')[0],
        vendor: expense.vendor || '',
        category: expense.category,
        paymentMethod: expense.paymentMethod || '',
        description: expense.description,
        notes: expense.notes || '',
      });
    }
  }, [expense]);

  const daysOld = expense
    ? Math.floor((Date.now() - new Date(expense.createdAt).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const daysRemaining = Math.max(0, 30 - daysOld);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.amount || parseFloat(form.amount) <= 0) {
      errs.amount = 'Amount must be positive';
    }
    if (!form.date) {
      errs.date = 'Date is required';
    }
    if (!form.description.trim()) {
      errs.description = 'Description is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      await updateExpense.mutateAsync({
        id: expenseId,
        data: {
          amount: parseFloat(form.amount),
          date: new Date(form.date).toISOString(),
          vendor: form.vendor.trim() || undefined,
          category: form.category,
          paymentMethod: form.paymentMethod || undefined,
          description: form.description.trim(),
          notes: form.notes.trim() || undefined,
        },
      });
      onSuccess();
    } catch (err: any) {
      const msg =
        err?.response?.status === 403
          ? 'Edit window expired — expenses cannot be modified after 30 days'
          : err.message || 'Failed to update expense';
      setErrors({ submit: msg });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">Edit Expense</h2>
            {daysRemaining > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining to edit
              </p>
            )}
          </div>
          <button onClick={onClose} className="claude-button claude-button-ghost p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loadingExpense ? (
          <div className="flex items-center justify-center py-12">
            <div className="claude-spinner"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Amount + Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className={`w-full pl-8 pr-4 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      errors.amount ? 'border-destructive' : 'border-border'
                    }`}
                  />
                </div>
                {errors.amount && <p className="text-xs text-destructive mt-1">{errors.amount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={`w-full px-3 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                    errors.date ? 'border-destructive' : 'border-border'
                  }`}
                />
              </div>
            </div>

            {/* Vendor */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Vendor</label>
              <input
                type="text"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Category + Payment Method */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Category *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Payment Method</label>
                <select
                  value={form.paymentMethod}
                  onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Description *</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={`w-full px-3 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.description ? 'border-destructive' : 'border-border'
                }`}
              />
              {errors.description && (
                <p className="text-xs text-destructive mt-1">{errors.description}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>

            {/* Audit info */}
            {expense && (
              <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                <p>Created: {new Date(expense.createdAt).toLocaleString()}</p>
                <p>Last updated: {new Date(expense.updatedAt).toLocaleString()}</p>
              </div>
            )}

            {/* Error */}
            {errors.submit && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {errors.submit}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="claude-button claude-button-ghost">
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateExpense.isPending}
                className="claude-button claude-button-primary disabled:opacity-50"
              >
                {updateExpense.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
